import * as React from 'react';
import { Link } from 'react-router-dom';

import { Badge } from './Badge';
import { InfoTooltip } from './Tooltip';
import { InlineSpinner } from '../assets/Spinner';
import { isAdmin, isAllModuleAccessUser } from '../pages/constants';
import { formatDateTime, getCardSummary } from '../utils';

interface PaymentDetailProps {
  order: any;
  disputeStatus: string | null;
  isLoadingDisputeStatus?: boolean;
}

const WON_DISPUTE_STATUSES = ['won', 'prevented'];

const isDisputeBlockingRefund = (disputeStatus: string | null): boolean => {
  if (disputeStatus === null) return false;
  if (isAdmin() || isAllModuleAccessUser()) return false;
  return !WON_DISPUTE_STATUSES.includes(disputeStatus.toLowerCase());
};

const getRefundEligibility = (charge: any, order: any, disputeStatus: string | null) => {
  if (isDisputeBlockingRefund(disputeStatus)) {
    return false;
  }
  if (order.id.startsWith('SHO-')) {
    return false;
  }
  if (order?.order_type === 'REPLACEMENT') {
    return false;
  }
  return charge?.status === 'SUCCESS';
};

export const PaymentDetail = ({
  order,
  disputeStatus,
  isLoadingDisputeStatus,
}: PaymentDetailProps) => {
  const { charge } = order;
  if (!charge) {
    return <></>;
  }

  const cardDetails = charge?.payload?.charges?.data?.[0]?.payment_method_details.card;
  const cardSummary = getCardSummary(charge?.card_number, charge?.card_type, cardDetails);
  const refundTotal = order.total_refunds ?? '0.00';
  const isRefundEligible = getRefundEligibility(charge, order, disputeStatus);
  const refundUnavailableReason =
    'Orders are not refundable if any of the following criteria are met: charge unsuccessful, charge has an ongoing or lost dispute, order is a replacement order';
  return (
    <>
      <div className="mb-1 flex items-center">
        <h2 className="inline-block font-bold">Payment Details</h2>
        {isRefundEligible && (
          <div className="text-sm ml-2">
            <Link className="cursor-pointer underline text-anchor" to="refund">
              Process a Refund
            </Link>
          </div>
        )}
        {!isRefundEligible && (
          <div className="ml-4 inline-flex text-sm">
            <Badge label="Refund Unavailable" variant="secondary" />
            <div className="ml-1">
              <div className="ml-1">
                <InfoTooltip text={refundUnavailableReason} />
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="bg-white border rounded-lg p-4 shadow-md w-full min-h-[262px]">
        <div className="mb-2">
          <strong>Payment Status:</strong> {charge?.status}
        </div>
        <div className="mb-2">
          <strong>Payment Timestamp:</strong> {formatDateTime(order.charged)}
        </div>
        <div className="mb-2">
          <strong>Payment Type:</strong> {order?.payment_type}
        </div>
        <div className="mb-2">
          <strong>Payment Card:</strong> {cardSummary ?? '--'}
        </div>
        <div className="mb-2">
          <strong>Payment Failure Reason:</strong> {charge.error ?? '--'}
        </div>
        <div className="mb-2">
          <strong>Refund Total:</strong> ${refundTotal} {order.currency}
        </div>
        <div className="mb-2">
          <strong>Payment Processor:</strong> {order.payment_processor}
        </div>
        <div className="mb-2">
          <strong>Dispute Status:</strong>{' '}
          {isLoadingDisputeStatus ? (
            <span className="inline-flex align-middle ml-1">
              <InlineSpinner />
            </span>
          ) : (
            <span className={disputeStatus ? 'text-red-500' : ''}>
              {disputeStatus?.toUpperCase() ?? '--'}
            </span>
          )}
        </div>
      </div>
    </>
  );
};
